import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { postRequest } from "../../system/server-call";
import { ToastifyService } from "../../services";

const CreatePassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!token) {
      setError("Invalid or missing setup link. Please request a new one.");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    if (password !== passwordConfirmation) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await postRequest("auth/setup-password", {
        token,
        password,
        password_confirmation: passwordConfirmation,
      });
      setDone(true);
      ToastifyService.showSuccess("Password created! You can now log in.");
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "Something went wrong. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-wrapper pt-5">
      <div className="auth-page-content">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-8 col-lg-6 col-xl-5">
              <div className="card mt-4 card-bg-fill">
                <div className="card-body p-4">
                  {done ? (
                    <div className="text-center py-4">
                      <div
                        style={{
                          width: 64,
                          height: 64,
                          borderRadius: "50%",
                          background: "#d1fae5",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0 auto 16px",
                        }}
                      >
                        <i
                          className="ri-check-line"
                          style={{ fontSize: 32, color: "#059669" }}
                        />
                      </div>
                      <h5 className="text-success mb-2">Password Created!</h5>
                      <p className="text-muted">
                        You will be redirected to the login page shortly…
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="text-center mt-2">
                        <h5 className="text-primary">Create Your Password</h5>
                        <p className="text-muted">
                          Set a secure password to activate your account.
                        </p>
                      </div>

                      <div className="p-2 mt-4">
                        <form onSubmit={handleSubmit} className="h-auto">
                          {/* Password */}
                          <div className="mb-3">
                            <label htmlFor="password" className="form-label">
                              Password
                            </label>
                            <div className="position-relative">
                              <input
                                type={showPassword ? "text" : "password"}
                                id="password"
                                className="form-control pe-5"
                                placeholder="At least 6 characters"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                              />
                              <button
                                type="button"
                                className="btn btn-link position-absolute top-50 end-0 translate-middle-y pe-3 text-muted"
                                style={{ border: "none", background: "none" }}
                                onClick={() => setShowPassword((v) => !v)}
                                tabIndex={-1}
                              >
                                <i
                                  className={
                                    showPassword
                                      ? "ri-eye-off-line"
                                      : "ri-eye-line"
                                  }
                                />
                              </button>
                            </div>
                          </div>

                          {/* Confirm Password */}
                          <div className="mb-3">
                            <label
                              htmlFor="password_confirmation"
                              className="form-label"
                            >
                              Confirm Password
                            </label>
                            <div className="position-relative">
                              <input
                                type={showConfirm ? "text" : "password"}
                                id="password_confirmation"
                                className="form-control pe-5"
                                placeholder="Re-enter password"
                                value={passwordConfirmation}
                                onChange={(e) =>
                                  setPasswordConfirmation(e.target.value)
                                }
                                required
                              />
                              <button
                                type="button"
                                className="btn btn-link position-absolute top-50 end-0 translate-middle-y pe-3 text-muted"
                                style={{ border: "none", background: "none" }}
                                onClick={() => setShowConfirm((v) => !v)}
                                tabIndex={-1}
                              >
                                <i
                                  className={
                                    showConfirm
                                      ? "ri-eye-off-line"
                                      : "ri-eye-line"
                                  }
                                />
                              </button>
                            </div>
                          </div>

                          {error && (
                            <div className="alert alert-danger py-2" role="alert">
                              {error}
                            </div>
                          )}

                          <div className="mt-4">
                            <button
                              type="submit"
                              className="btn btn-success w-100"
                              disabled={loading}
                            >
                              {loading ? (
                                <>
                                  <Spinner
                                    animation="border"
                                    size="sm"
                                    className="me-2"
                                  />
                                  Setting password…
                                </>
                              ) : (
                                "Create Password"
                              )}
                            </button>
                          </div>
                        </form>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="mt-4 text-center">
                <p className="mb-0 text-muted">
                  Remembered your credentials?{" "}
                  <Link
                    to="/login"
                    className="fw-semibold text-primary text-decoration-underline"
                  >
                    Sign In
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePassword;
